/**
 * Configuração base da API
 */

import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { SessionUtil } from '@utils/session.util';

class ApiService {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
      timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.instance.interceptors.request.use(
      (config) => {
        // Adiciona token ou outras configurações se necessário
        const vendedor = SessionUtil.getVendedor();
        
        if (vendedor?.vendCodigo && config.headers) {
          config.headers['X-Vendedor-Codigo'] = vendedor.vendCodigo;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error: AxiosError) => {
        return Promise.reject(this.handleError(error));
      }
    );
  }

  private handleError(error: AxiosError): ApiError {
    if (error.response) {
      // Erro com resposta do servidor
      return {
        message: this.getErrorMessage(error.response),
        status: error.response.status,
        code: error.code,
        data: error.response.data,
      };
    } else if (error.request) {
      // Erro de rede
      return {
        message: 'Erro de conexão. Verifique sua internet.',
        status: 0,
        code: 'NETWORK_ERROR',
      };
    } else {
      // Outro tipo de erro
      return {
        message: error.message || 'Erro desconhecido',
        status: 0,
        code: 'UNKNOWN_ERROR',
      };
    }
  }

  private getErrorMessage(response: AxiosResponse): string {
    const data = response.data;

    // Tenta diferentes formatos de mensagem de erro
    if (data?.mensagem) return data.mensagem;
    if (data?.message) return data.message;
    if (data?.error) return data.error;
    if (typeof data === 'string') return data;

    // Mensagens padrão por status
    switch (response.status) {
      case 400:
        return 'Requisição inválida';
      case 401:
        return 'Não autorizado';
      case 403:
        return 'Acesso negado';
      case 404:
        return 'Recurso não encontrado';
      case 500:
        return 'Erro interno do servidor';
      case 503:
        return 'Serviço indisponível';
      default:
        return 'Ocorreu um erro inesperado';
    }
  }

  /**
   * GET request
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.get<T>(url, config);
    return response.data;
  }

  /**
   * POST request
   */
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.post<T>(url, data, config);
    return response.data;
  }

  /**
   * PUT request
   */
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.put<T>(url, data, config);
    return response.data;
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.delete<T>(url, config);
    return response.data;
  }

  /**
   * PATCH request
   */
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.patch<T>(url, data, config);
    return response.data;
  }

  /**
   * Obtém a instância do axios para casos especiais
   */
  getInstance(): AxiosInstance {
    return this.instance;
  }
}

export interface ApiError {
  message: string;
  status: number;
  code?: string;
  data?: any;
}

export const api = new ApiService();